// Hand it to somebody. Every responder, with a box to find them. Lifted out
// of the Andon app's `AlertScreen`, where it was a local component.
//
// The list was once the ROUTING TABLE — the managers and specialists the
// ladder calls — which is a different question from "who can I give this to".
// It is the Responder group, which on a real floor is dozens of people, so it
// needs a search rather than a scroll: a name you have to hunt for is a
// handover somebody does not bother making.
//
// `tall` rather than the short sheet, because a list of two and a list of
// forty should not be two different shapes, and the search box wants the
// keyboard to have somewhere to go.

import { useEffect, useMemo, useState } from 'react';
import { andonAvatarUrl } from '@/api/andon/client';
import { Sheet } from '@/components/ui/Sheet';
import { useT } from '@/i18n/useT';
import { matches } from '@/lib/mentions';
import { useResponders } from '@/queries/andon';
import { OwnerAvatar } from './primitives';

export function HandoverSheet({
  open,
  onClose,
  onPick,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (login: string) => void;
  pending: boolean;
}) {
  const t = useT();
  const [term, setTerm] = useState('');
  const { data, isLoading } = useResponders(open);

  // The same word-prefix matching the @ picker uses, so "cur" reaches Ben
  // Curtis in both places and neither surprises somebody who has learned the
  // other.
  const shown = useMemo(() => matches(data ?? [], term.trim(), 50), [data, term]);

  // A sheet that reopens holding the last search is one somebody has to clear
  // before they can use it.
  useEffect(() => {
    if (!open) setTerm('');
  }, [open]);

  return (
    <Sheet open={open} onClose={onClose} variant="tall" title={t.handoverTitleT}>
      <p style={{ margin: '0 0 12px', font: '500 12px/1.6 Manrope,sans-serif', color: 'var(--sub)' }}>{t.handoverLeadT}</p>
      <input
        data-autofocus
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder={t.handoverSearchPhT}
        aria-label={t.handoverSearchPhT}
        style={{ width: '100%', height: 46, padding: '0 14px', borderRadius: 13, background: 'var(--surface2)', border: '1px solid var(--line)', color: 'var(--ink)', font: '500 13.5px Manrope,sans-serif', marginBottom: 12, outline: 'none' }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {shown.map((p) => (
          <button
            key={p.login}
            type="button"
            className="press-row"
            disabled={pending}
            onClick={() => onPick(p.login)}
            style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 14px', borderRadius: 13, background: 'var(--surface2)', width: '100%', textAlign: 'left' }}
          >
            <OwnerAvatar name={p.name} size={30} photoUrl={andonAvatarUrl(p)} />
            <span style={{ font: '600 13px Manrope,sans-serif', color: 'var(--ink)' }}>{p.name}</span>
          </button>
        ))}
        {!shown.length && (
          <p style={{ margin: '8px 2px', font: '500 12px Manrope,sans-serif', color: 'var(--sub)' }}>
            {isLoading ? t.handoverReadingT : term.trim() ? `${t.handoverNobodyMatchesT} “${term.trim()}”.` : t.handoverNoneConfiguredT}
          </p>
        )}
      </div>
    </Sheet>
  );
}
